import { captureException } from '@sentry/react';

type ReactErrorInfo = { componentStack?: string | null };

/**
 * React 19 stopped re-throwing render errors: uncaught ones go to `window.reportError` and
 * boundary-caught ones to `console.error`. Passing `onUncaughtError` / `onCaughtError` to `createRoot`
 * *replaces* those defaults rather than supplementing them, so we call `captureException` here and
 * then explicitly re-run React's default ourselves — never both-or-neither.
 *
 * This is not belt-and-braces: we never `init()` Sentry in this repo, the host page owns the client,
 * and `@sentry/react`'s carrier is keyed by exact SDK version, so whether our `captureException` call
 * reaches a live client at all depends on whether the host's Sentry shares our version. If it is a
 * silent no-op, `captureException` alone would make a render crash report nowhere — worse than doing
 * nothing. Reporting to Sentry and preserving the default keeps both paths independent: one failing
 * (`captureException` throwing, or being a no-op) must not suppress the other, so `captureException`
 * is wrapped and the default always runs regardless of its outcome. Wired into both `createRoot`
 * calls in `src/index.tsx`.
 */
export const reportReactError =
	(kind: 'uncaught' | 'caught') => (error: unknown, errorInfo: ReactErrorInfo) => {
		try {
			captureException(error, {
				extra: { reactComponentStack: errorInfo.componentStack },
				tags: { source: `Unified Header | ${kind} render error` },
			});
		} catch {
			// Our reporting must never be able to suppress React's default report below.
		}

		if (kind === 'uncaught') {
			if (typeof reportError === 'function') {
				reportError(error);
			} else {
				console.error(error);
			}
		} else {
			// A boundary-caught error is handled by definition — escalating it to `reportError` would
			// show the host errors it should not see. React's own default here is `console.error`.
			console.error(error);
		}
	};
